/**
 * Widget Registry
 * Maps widget IDs to their components and metadata
 */

import { WidgetId } from "../types";
import {
  RetirementReadinessWidget,
  AUMByProductWidget,
  ContributionRateWidget,
  SocialSecurityWidget,
  RetirementIncomeSourcesWidget,
  FEGLIRiskWidget,
  AgeDistributionWidget,
  RolloverOpportunityWidget,
  EmployerMatchWidget,
  LocationInsightsWidget,
  PlanTypeSpreadWidget,
  LongevityStressWidget,
} from "../widgets";
import React from "react";
import { ClientType } from "../../../types";

export interface WidgetMetadata {
  id: WidgetId;
  title: string;
  description: string;
  category: "core" | "insights";
}

export const WIDGET_METADATA: Record<WidgetId, WidgetMetadata> = {
  "retirement-readiness": {
    id: "retirement-readiness",
    title: "Retirement Readiness Overview",
    description: "Distribution of clients across retirement readiness categories",
    category: "core",
  },
  "aum-by-product": {
    id: "aum-by-product",
    title: "AUM by Product Type",
    description: "Assets under management by product type",
    category: "core",
  },
  "contribution-rate": {
    id: "contribution-rate",
    title: "Contribution Rate Distribution",
    description: "Average contribution rates by age group",
    category: "core",
  },
  "social-security": {
    id: "social-security",
    title: "Social Security",
    description: "Social Security benefit analysis",
    category: "core",
  },
  "retirement-income-sources": {
    id: "retirement-income-sources",
    title: "Retirement Income Sources",
    description: "Average annual income by source",
    category: "core",
  },
  "fegli-risk": {
    id: "fegli-risk",
    title: "FEGLI Risk",
    description: "FEGLI coverage risk assessment",
    category: "core",
  },
  "age-distribution": {
    id: "age-distribution",
    title: "Age Distribution",
    description: "Client age distribution analysis",
    category: "insights",
  },
  "rollover-opportunity": {
    id: "rollover-opportunity",
    title: "Rollover Opportunities",
    description: "Potential rollover opportunities",
    category: "insights",
  },
  "employer-match": {
    id: "employer-match",
    title: "Employer Match",
    description: "Employer matching contribution analysis",
    category: "insights",
  },
  "location-insights": {
    id: "location-insights",
    title: "Location Insights",
    description: "Geographic distribution and insights",
    category: "insights",
  },
  "plan-type-spread": {
    id: "plan-type-spread",
    title: "Retirement Plan Type Spread",
    description: "Distribution of retirement plan types",
    category: "insights",
  },
  "longevity-stress": {
    id: "longevity-stress",
    title: "Longevity Stress",
    description: "Longevity risk and funding analysis",
    category: "insights",
  },
};

export interface WidgetComponentProps {
  clients: ClientType[];
  isLoading?: boolean;
  preCalculatedData?: any;
}

type WidgetComponent = React.ComponentType<WidgetComponentProps>;

// Widget components are now HOC-wrapped in widgetComponentsWrapped.ts
// Import directly from widgetComponentsWrapped.ts to avoid circular dependency

/**
 * Gets widget title by ID
 */
export function getWidgetTitle(widgetId: WidgetId): string {
  return WIDGET_METADATA[widgetId]?.title || widgetId;
}

/**
 * Gets widget metadata by ID
 */
export function getWidgetMetadata(widgetId: WidgetId): WidgetMetadata | undefined {
  return WIDGET_METADATA[widgetId];
}
